// Create basic homepage component

import { ReactElement, useState } from "react"
import { useDispatch } from "react-redux"

import PublicHomepage from "./publicHomepage"
import UnapprovedUserHomepage from "./unapprovedUserHomepage"
import DashboardHomepageSkeleton from "../../components/loading/skeletons/dashboardHomepageSkeleton"
import CompanyDetailsPopup from "../../components/popUps/companyDetails/companyDetailsPopup"
import {
    DB_ASSESSOR_DEMAND,
    DB_ASSESSOR_GEN,
    DB_ASSESSOR_V2G,
    DNO_APPROVED,
    ENA_SYS_ADMIN,
    INSTALLER,
    INSTALLER_ADMIN,
    INSTALLER_APPROVED,
    LCT_MANU_ADMIN,
    LCT_MANU_APPROVED,
} from "../../constants/roles"
import { useGetState } from "../../hooks/useGetState"
import { useGetUserData } from "../../hooks/useGetUserData"
import {
    getShowCompanyDetailsPref,
    setShowCompanyDetailsPref,
    getShowMFA,
} from "../../redux/preferencesSlice"
import useGetMyOrg from "../../services/middleware/hooks/myOrganisation/useGetMyOrg"
import { snackbar } from "../../utils/general/generalUtils"
import DnoHomepage from "../dnoPortal/dnoHomepage"
import InstallerHomepage from "../installerPortal/installerHomepage"
import ManufacturerHomepage from "../manufacturerPortal/manufacturerHomepage"
import SysAdminHomepage from "../sysAdmin/sysAdminHomepage"
import DemandAssessorHomepage from "../validatorsPortal/demandAssessorHomepage"
import GenerationAssessorHomepage from "../validatorsPortal/generationAssessorHomepage"
import V2gAssessorHomepage from "../validatorsPortal/v2gAssessorHomepage"

const HOMEPAGE_DISPATCHER: { [key: string]: ReactElement } = {
    [DNO_APPROVED]: <DnoHomepage />,
    [ENA_SYS_ADMIN]: <SysAdminHomepage />,
    [LCT_MANU_APPROVED]: <ManufacturerHomepage />,
    [DB_ASSESSOR_DEMAND]: <DemandAssessorHomepage />,
    [DB_ASSESSOR_GEN]: <GenerationAssessorHomepage />,
    [DB_ASSESSOR_V2G]: <V2gAssessorHomepage />,
    [INSTALLER_APPROVED]: <InstallerHomepage />,
}

/*
/*
* Initial homepage component for user navigation
* If user is signed in and has a group listed within groupComponents, 
* they will be redirected to their dashboard 
* 
* example usage: <Homepage />
* @returns {ReactElement} React component
*/
const Homepage = (): ReactElement => {
    const dispatch = useDispatch()
    const {
        groups,
        showUnapprovedWarning,
        isAwaitingSetup,
        isSignedIn,
        isConfiguring,
    } = useGetUserData()
    const { isLoading, data } = useGetMyOrg() // Call the hook to fetch organization details
    const [isPopupSubmitted, setIsPopupSubmitted] = useState(false)
    const showCompanyDetailsPref = useGetState(getShowCompanyDetailsPref)
    const showMFA = useGetState(getShowMFA)

    const handleSetIsOpen = (): void => {
        dispatch(setShowCompanyDetailsPref())
        snackbar(
            "We will remind you to complete these details in 30 days",
            "default"
        )
    }
    const handlePopupSubmit = (): void => {
        setIsPopupSubmitted(true)
    }
    const isAdmin =
        groups.includes(INSTALLER_ADMIN) || groups.includes(LCT_MANU_ADMIN)

    const isOrganisationDetailsIncomplete =
        !data?.organisation?.organisationName ||
        !data?.organisation?.organisationAddress ||
        !data?.organisation?.organisationPostcode ||
        !data?.organisation?.organisationContactNumber
    // Case 1: Admin with incomplete organization details (with close button)
    if (
        !showMFA &&
        isSignedIn &&
        !isLoading &&
        !isPopupSubmitted &&
        showCompanyDetailsPref &&
        isOrganisationDetailsIncomplete &&
        (groups.includes(INSTALLER) || groups.includes(LCT_MANU_APPROVED)) &&
        isAdmin
    ) {
        return (
            <CompanyDetailsPopup
                isOpen
                isEditOrganisation
                onSubmit={handlePopupSubmit}
                data={data}
                setIsOpen={handleSetIsOpen}
                isAdmin={isAdmin}
            />
        )
    }

    // Case 2: Non-admin with missing installerIndividualPhoneNumber (with close button)
    if (
        !showMFA &&
        isSignedIn &&
        !isLoading &&
        showCompanyDetailsPref &&
        !isPopupSubmitted &&
        !isOrganisationDetailsIncomplete &&
        !isAdmin &&
        (groups.includes(INSTALLER) || groups.includes(LCT_MANU_APPROVED)) &&
        (!data?.organisation?.installerIndividualPhoneNumber ||
            data?.organisation?.installerIndividualPhoneNumber.trim() === "")
    ) {
        return (
            <CompanyDetailsPopup
                isOpen
                isEditOrganisation
                onSubmit={handlePopupSubmit}
                data={data}
                setIsOpen={handleSetIsOpen}
                isAdmin={isAdmin}
            />
        )
    }

    // Case 3: Admin with complete org details but missing installerIndividualPhoneNumber (with close button)
    if (
        !showMFA &&
        isSignedIn &&
        !isLoading &&
        showCompanyDetailsPref &&
        !isPopupSubmitted &&
        !isOrganisationDetailsIncomplete &&
        isAdmin &&
        (groups.includes(INSTALLER) || groups.includes(LCT_MANU_APPROVED)) &&
        (!data?.organisation?.installerIndividualPhoneNumber ||
            data?.organisation?.installerIndividualPhoneNumber.trim() === "")
    ) {
        return (
            <CompanyDetailsPopup
                isOpen
                isEditOrganisation
                onSubmit={handlePopupSubmit}
                data={data}
                setIsOpen={handleSetIsOpen}
                isAdmin={isAdmin}
            />
        )
    }

    if (isAwaitingSetup && isSignedIn) {
        return <UnapprovedUserHomepage awaitingSetUp />
    }

    const groupName = groups.find((group) => HOMEPAGE_DISPATCHER[group])
    if (groupName) {
        return HOMEPAGE_DISPATCHER[groupName]
    }

    const installerSoleTrader = groups.every(
        (installerGroup) => installerGroup === INSTALLER
    )
    if (installerSoleTrader) {
        return HOMEPAGE_DISPATCHER[INSTALLER_APPROVED]
    }

    if (showUnapprovedWarning) {
        return <UnapprovedUserHomepage awaitingSetUp={false} />
    }

    if (isConfiguring) {
        return <DashboardHomepageSkeleton />
    }

    return <PublicHomepage />
}

export default Homepage
